import CanvasChart from './-canvas-chart.ts';
import ChartTooltip from './-chart-tooltip.gts';
import { concat } from '@ember/helper';
import style from 'ember-style-modifier/modifiers/style';
import {
  setupCanvas,
  drawGridLines,
  drawYAxisLabels,
  drawXAxisLabels,
  drawAreaFill,
} from '../../utils/charts/canvas-utils.ts';
import { linearScale, niceAxisTicks } from '../../utils/charts/chart-scales.ts';
import { computeLayout, formatAxisValue, formatNumber } from '../../utils/charts/chart-layout.ts';
import { DEFAULT_CHART_COLORS, getThemeColors } from '../../utils/charts/color-palette.ts';
import type { ChartDataPoint, ChartOptions, AxisOptions } from '../../types/charts.ts';

interface LineChartSignature {
  Args: ChartOptions &
    AxisOptions & {
      /** Chart data points */
      data: ChartDataPoint[];
      /** Fixed height in pixels (default: 300) */
      height?: number;
      /** Show point markers (default: true) */
      showPoints?: boolean;
      /** Line stroke width (default: 2) */
      strokeWidth?: number;
    };
  Element: HTMLDivElement;
}

export default class LineChart extends CanvasChart<LineChartSignature> {
  get color() {
    return this.args.colors?.[0] ?? DEFAULT_CHART_COLORS[1]!;
  }

  get data() {
    return this.args.data ?? [];
  }

  get showPoints() {
    return this.args.showPoints !== false;
  }

  get tooltipData() {
    if (this.hoveredIndex === null || !this.data[this.hoveredIndex]) return null;
    const d = this.data[this.hoveredIndex]!;
    return {
      category: d.category,
      value: this.args.formatValue?.(d.value) ?? formatNumber(d.value),
    };
  }

  private pointPositions: Array<{ x: number; y: number; index: number }> = [];

  /**
   * Repaints the chart at its measured size and refreshes pointer hit regions.
   * @param canvas - Mounted canvas owned by the chart lifecycle.
   * @returns No value; replaces the visible marks and axes.
   */
  draw(canvas: HTMLCanvasElement) {
    const width = this.containerWidth;
    const height = this.height;
    const data = this.data;
    if (!data.length) return;

    const shouldRotate = false;
    const bottomMargin = 44;
    const layout = computeLayout(width, height, { bottom: bottomMargin, left: 55 });
    const theme = getThemeColors();

    const ctx = setupCanvas(canvas, width, height);
    ctx.clearRect(0, 0, width, height);

    const maxVal = Math.max(...data.map((d) => d.value), 0);
    const ticks = niceAxisTicks(0, maxVal, 5, this.args.minimumTickStep);
    const formatFn = this.args.formatValue ?? formatAxisValue;
    ctx.font = '500 12px system-ui, sans-serif';
    layout.left = Math.max(
      layout.left,
      ...ticks.map((tick) => ctx.measureText(formatFn(tick)).width + 16)
    );
    layout.innerWidth = Math.max(0, width - layout.left - layout.right);
    const yScale = linearScale(
      [0, ticks[ticks.length - 1]!],
      [layout.top + layout.innerHeight, layout.top]
    );

    // X positions — evenly spaced
    const xStep = data.length > 1 ? layout.innerWidth / (data.length - 1) : 0;
    const xPositions = data.map((_, i) => layout.left + i * xStep);

    drawGridLines(ctx, ticks, yScale, layout.left, layout.left + layout.innerWidth, theme.grid);

    // Y axis
    drawYAxisLabels(ctx, ticks, yScale, layout.left - 8, theme.textMuted, formatFn);

    // X axis (with thinning for dense data)
    const labels = data.map((d) => d.category);
    const maxLabels = Math.max(4, Math.floor(layout.innerWidth / 96));
    drawXAxisLabels(
      ctx,
      labels,
      xPositions,
      layout.top + layout.innerHeight + 8,
      theme.textMuted,
      shouldRotate,
      maxLabels
    );

    this.pointPositions = data.map((d, i) => ({
      x: xPositions[i]!,
      y: yScale(d.value),
      index: i,
    }));

    // Subtle area fill behind line — Peel-style gradient
    const baseline = layout.top + layout.innerHeight;
    drawAreaFill(
      ctx,
      this.pointPositions.map((p) => ({ x: p.x, y: p.y })),
      baseline,
      this.color,
      0.08, // very subtle
      0.01
    );

    // Draw line - cap stroke width for sparse data to prevent fat lines
    const baseStrokeWidth = this.args.strokeWidth ?? 2;
    const maxStrokeWidth = data.length <= 3 ? Math.min(baseStrokeWidth, 3) : baseStrokeWidth;
    const strokeWidth = Math.min(maxStrokeWidth, 4);
    ctx.strokeStyle = this.color;
    ctx.lineWidth = strokeWidth;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.beginPath();
    this.pointPositions.forEach((p, i) => {
      if (i === 0) ctx.moveTo(p.x, p.y);
      else ctx.lineTo(p.x, p.y);
    });
    ctx.stroke();

    // Draw points (auto-hidden when data is too dense)
    if (this.showPoints && data.length <= 50) {
      this.pointPositions.forEach((p, i) => {
        const isHovered = this.hoveredIndex === i;
        const radius = isHovered ? 6 : 4;

        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
        ctx.fill();

        // White inner dot
        ctx.fillStyle = theme.tooltipBg;
        ctx.beginPath();
        ctx.arc(p.x, p.y, radius - 2, 0, Math.PI * 2);
        ctx.fill();
      });
    }

    // Crosshair for hovered point
    if (this.hoveredIndex !== null && this.pointPositions[this.hoveredIndex]) {
      const p = this.pointPositions[this.hoveredIndex]!;
      ctx.strokeStyle = theme.grid;
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);

      ctx.beginPath();
      ctx.moveTo(p.x, layout.top);
      ctx.lineTo(p.x, layout.top + layout.innerHeight);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(layout.left, p.y);
      ctx.lineTo(layout.left + layout.innerWidth, p.y);
      ctx.stroke();

      ctx.setLineDash([]);
    }

    // Axis labels
    if (this.args.xLabel) {
      ctx.fillStyle = theme.text;
      ctx.font = '12px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(this.args.xLabel, layout.left + layout.innerWidth / 2, height - 4);
    }
    if (this.args.yLabel) {
      ctx.save();
      ctx.fillStyle = theme.text;
      ctx.font = '12px system-ui, sans-serif';
      ctx.translate(14, layout.top + layout.innerHeight / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.textAlign = 'center';
      ctx.fillText(this.args.yLabel, 0, 0);
      ctx.restore();
    }
  }

  handleMouseMove(e: MouseEvent, canvas: HTMLCanvasElement) {
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    let closest: { dist: number; index: number } | null = null;
    for (const p of this.pointPositions) {
      const dist = Math.hypot(mx - p.x, my - p.y);
      if (dist < 20 && (!closest || dist < closest.dist)) {
        closest = { dist, index: p.index };
      }
    }

    if (closest) {
      if (this.hoveredIndex !== closest.index) {
        this.hoveredIndex = closest.index;
        const p = this.pointPositions[closest.index]!;
        this.tooltipX = rect.left + p.x;
        this.tooltipY = rect.top + p.y - 8;
        this.draw(canvas);
      }
    } else if (this.hoveredIndex !== null) {
      this.hoveredIndex = null;
      this.draw(canvas);
    }
  }

  handleClick(e: MouseEvent, canvas: HTMLCanvasElement) {
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    let closest: { dist: number; index: number } | null = null;
    for (const p of this.pointPositions) {
      const dist = Math.hypot(mx - p.x, my - p.y);
      if (dist < 20 && (!closest || dist < closest.dist)) {
        closest = { dist, index: p.index };
      }
    }

    if (closest && this.args.onClick && this.data[closest.index]) {
      this.args.onClick(this.data[closest.index]!);
    }
  }

  <template>
    <div
      data-test-chart
      data-test-chart-type="line"
      class="relative w-full h-full"
      {{this.installCanvas}}
      ...attributes
    >
      <canvas
        {{style width=(concat this.containerWidth "px") height=(concat this.height "px")}}
      ></canvas>

      {{#if this.tooltipData}}
        <ChartTooltip
          @x={{this.tooltipX}}
          @y={{this.tooltipY}}
          class="-translate-x-1/2 -translate-y-full"
          data-test-chart-tooltip
        >
          <div class="font-medium">{{this.tooltipData.category}}</div>
          <div class="tio-text-muted">{{this.tooltipData.value}}</div>
        </ChartTooltip>
      {{/if}}
    </div>
  </template>
}

export type { LineChartSignature };
